// src/modules/app-views/account/pages/Init/modals/RemoveAccountModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useMemo } from 'react';
import { ExclamationCircleOutlined, UserDeleteOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';
import authService from 'services/authService';
import FirebaseService from 'services/FirebaseService';
import { mergeUrlParamsQuery } from 'utils/urlUtils';
import { API } from 'constants/Api';
import noticeStep from './Step1';
import passwordStep from './Step2';
import confirmStep from './Step3';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const RemoveAccountModal = ({ show, setShow, afterClose }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedUser = useZustand('auth', 'user');
  const storedSubscriptions = useZustand('auth', 'subscriptions');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const userEmail = storedUser?.email?.value || storedUser?.email || '';
  const hasSubscriptions = storedSubscriptions.length > 0;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // If the user has subscriptions, show warning modal instead
  useEffect(() => {
    if (show && hasSubscriptions) {
      appService.modal({
        flag: 'warning',
        title: 'remove_account_prohibit_title',
        icon: <ExclamationCircleOutlined />,
        content: (
          <div>
            <p>{translateX('remove_account_description')}</p>
          </div>
        ),
        okText: 'close',
        centered: true,
        maskClosable: true,
      });
      setShow(false);
    }
  }, [show, hasSubscriptions, setShow]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async () => {
    const setLoadingAppStore = useZustand('boot', 'setLoadingApp', false);
    const { del } = useZustand('global', 'useApi', false);

    try {
      // 1. Show loading screen
      setLoadingAppStore({
        visible: true,
        title: 'removing_your_account',
        translucid: false,
        delayToHide: 1000,
      });

      // 2. Delete user from Firebase Authentication
      // COMMENTED: With the new architecture, production and homologation share the same
      // Firebase Authentication. Removing a user from one environment would break access
      // to the other. The app is prepared to reuse existing Firebase users.
      // try {
      //   const currentUser = FirebaseService.getCurrentUser();
      //   if (currentUser?.uid) {
      //     await FirebaseService.deleteUserFromAuth(currentUser.uid);
      //   }
      // } catch (firebaseErr) {
      //   console.error('[RemoveAccountModal] Firebase deletion error:', firebaseErr);
      //   // Continue even if Firebase deletion fails, we'll delete from backend
      // }

      // 3. Delete account from backend
      await del({
        url: mergeUrlParamsQuery({
          url: API.ACCOUNTS.URL,
          params: { _id: storedUser._id },
        }),
        headers: { 'x-forcepermanentlydeletation': true },
        showSuccessNotification: false,
      });

      // 4. Store user info for notification
      const name = storedUser?.name || '';
      const email = storedUser?.email || '';

      // 5. Sign out (will navigate to login and hide loading)
      await authService.signOut({ replace: true });

      // 6. Show success notification after logout
      setTimeout(() => {
        appService.notification({
          flag: 'info',
          title: 'removal_account_success_title',
          description: translateX('removal_account_success_description', {
            name: <strong>{`${name} <${email?.value}>`}</strong>,
          }),
          duration: 0,
        });
      }, 500);
    } catch (e) {
      console.error('[RemoveAccountModal] Error:', e);

      // Hide loading on error
      setLoadingAppStore({ visible: false, extendedDelayToHide: 0 });

      await appService.message({ flag: 'e', text: 'account_removal_failure' });
      throw e; // Re-throw to prevent modal from closing
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const steps = useMemo(
    () => [
      // Step 1: Notice
      noticeStep(),

      // Step 2: Password
      passwordStep({ storedUser }),

      // Step 3: Confirm
      confirmStep(),
    ],
    [storedUser]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  // Don't render the modal if user has subscriptions (warning is shown via useEffect)
  if (hasSubscriptions) return null;

  return (
    <GenericModal
      show={show}
      setShow={setShow}
      icon={<UserDeleteOutlined />}
      title="account_removal"
      steps={steps}
      onFinish={handleFinish}
      initialValues={{ email: userEmail }}
      afterClose={afterClose}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default RemoveAccountModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
